import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, ErrorBanner, MultiPicker, Pill, fmt } from "../components/ui";
import {
  api,
  apiUrl,
  type Campaign,
  type EnrolReply,
  type FieldOption,
  type Rule,
  type RulePreview,
  type Segment,
} from "../lib/api";

/** Operators offered per field type. Kept in step with the compiler's
 *  registry — the field list itself comes from the API, so the builder can
 *  never offer a field the backend cannot compile. */
/** Does this field store an id and show a name?
 *
 *  Decided from the data rather than a hard-coded list of field names, so a
 *  new reference field gets the right control without anyone remembering to
 *  add it here. */
const isReference = (opts: Array<{ value: string; label: string }>) =>
  opts.length > 0 && opts.some((o) => o.label !== o.value);

const OPS: Record<string, Array<{ op: string; label: string; value?: "one" | "many" | "range" | "none" }>> = {
  text: [
    { op: "eq", label: "is", value: "one" },
    { op: "neq", label: "is not", value: "one" },
    { op: "in", label: "is one of", value: "many" },
    { op: "not_in", label: "is none of", value: "many" },
    { op: "contains", label: "contains", value: "one" },
    { op: "is_null", label: "is empty", value: "none" },
    { op: "not_null", label: "is not empty", value: "none" },
  ],
  enum: [
    { op: "eq", label: "is", value: "one" },
    { op: "neq", label: "is not", value: "one" },
    { op: "in", label: "is one of", value: "many" },
    { op: "not_in", label: "is none of", value: "many" },
  ],
  number: [
    { op: "gte", label: "is at least", value: "one" },
    { op: "lte", label: "is at most", value: "one" },
    { op: "eq", label: "is exactly", value: "one" },
    { op: "between", label: "is between", value: "range" },
  ],
  date: [
    { op: "within", label: "was in the last", value: "one" },
    { op: "not_within", label: "was NOT in the last", value: "one" },
    { op: "is_null", label: "never happened", value: "none" },
  ],
  datetime: [
    { op: "within", label: "was in the last", value: "one" },
    { op: "not_within", label: "was NOT in the last", value: "one" },
    { op: "is_null", label: "never happened", value: "none" },
  ],
  // Tags are not a column, so they get their own operators. The compiler has
  // always understood them; the builder just never offered the field.
  tag: [
    { op: "has_tag", label: "is", value: "one" },
    { op: "has_any_tag", label: "is any of", value: "many" },
    { op: "has_all_tags", label: "is all of", value: "many" },
    // Excluding a tag is at least as common as requiring one - "everyone
    // except the ones we already messaged" - and there was no way to say it,
    // so that audience could not be built here at all.
    { op: "lacks_tag", label: "is NOT", value: "one" },
    { op: "lacks_any_tag", label: "is none of", value: "many" },
  ],
};

/** Fields that hold a PERSON, stored as a user id.
 *
 *  They are numbers in the database, so going by kind alone offered "is at
 *  least", "is at most" and "is between" - "Owner is at least 29" - which is
 *  not a question anyone means. The API lists these two as people by name
 *  (/field-values special-cases exactly them), so they are asked about the way
 *  a choice from a list is. */
const PERSON_FIELDS = new Set(["contact.owner_user_id", "contact.last_closer_user_id"]);

/** Durations "was in the last" understands. Offered instead of the field's
 *  actual stored dates, which is what the box used to suggest - and every one
 *  of those was rejected as "not a duration". */
const DURATION_PRESETS = ["1d", "7d", "14d", "30d", "60d", "90d", "6m", "1y"];

function opsFor(field: FieldOption | undefined) {
  if (field && PERSON_FIELDS.has(field.key)) return OPS.enum;
  return OPS[field?.kind ?? "text"] ?? OPS.text;
}

type Row = { field: string; op: string; value?: string; min?: string; max?: string };

// The reverse of toRule, so a saved audience can be reopened and changed
// rather than rebuilt from memory.
function fromRule(rule: Rule | undefined): Row[] {
  if (!rule || !rule.op) return [];
  const nodes = rule.op === "and" || rule.op === "or" ? rule.children ?? [] : [rule];
  return (nodes as Rule[])
    .filter((n) => n.field || String(n.op).includes("tag"))
    .map((n) => ({
      field: n.field ? String(n.field) : "tag",
      op: String(n.op),
      value:
        n.value === undefined
          ? ""
          : Array.isArray(n.value)
            ? n.value.join(", ")
            : String(n.value),
      min: n.min === undefined ? "" : String(n.min),
      max: n.max === undefined ? "" : String(n.max),
    }));
}

function toRule(rows: Row[], join: "and" | "or" = "and"): Rule {
  const children = rows
    .filter((r) => r.field && r.op)
    .map((r) => {
      // has_tag and friends take a value and no field - passing one would be
      // rejected by the compiler as an unknown field.
      const node: Rule =
        r.field === "tag" ? ({ op: r.op } as Rule) : { op: r.op, field: r.field };
      if (r.op === "between") {
        node.min = Number(r.min ?? 0);
        node.max = Number(r.max ?? 0);
      } else if (
        r.op === "in" ||
        r.op === "not_in" ||
        // has_any_tag / has_all_tags take a LIST. They used to fall through to
        // the scalar branch below, so the builder's own multi-select sent
        // "replied,customer" as one string and matched nobody - 0 on screen
        // where 40 people carried a tag.
        r.op === "has_any_tag" ||
        r.op === "has_all_tags" ||
        r.op === "lacks_any_tag"
      ) {
        // several values on one field, which is the OR people actually want
        // most of the time - "California or Texas" without nesting anything
        node.value = (r.value ?? "")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
      } else if (!["is_null", "not_null"].includes(r.op)) {
        const n = Number(r.value);
        node.value = r.value !== "" && !Number.isNaN(n) ? n : r.value;
      }
      return node;
    });
  if (children.length === 0) return {} as Rule;
  if (children.length === 1) return children[0];
  return { op: join, children };
}

export function Audiences() {
  const [fields, setFields] = useState<FieldOption[]>([]);
  // Opens on a field that actually holds data. "Last flew" was the default
  // and nothing writes it, so the builder greeted everyone with nobody matched
  // and looked broken before a single choice was made.
  // A rule can arrive in the URL. Without this, "Build an audience" after an
  // import dropped you on an empty builder and you had to rebuild by hand the
  // filter the previous screen had just told you to use - which is the moment
  // somebody gives up and blasts the wrong list.
  const [params] = useSearchParams();
  const [rows, setRows] = useState<Row[]>(() => {
    const raw = params.get("rule");
    if (raw) {
      try {
        const parsed = fromRule(JSON.parse(raw) as Rule);
        if (parsed.length) return parsed;
      } catch {
        /* a malformed rule in a URL is not worth an error screen */
      }
    }
    const tag = params.get("tag");
    if (tag) return [{ field: "tag", op: "has_tag", value: tag }];
    return [{ field: "contact.last_reply_at", op: "within", value: "30d" }];
  });
  // Whether the conditions are ANDed or ORed. One toggle rather than a
  // per-row joiner: mixing them without brackets reads ambiguously, and
  // "is one of" already covers the common case of OR on a single field.
  const [join, setJoin] = useState<"and" | "or">("and");
  const [preview, setPreview] = useState<RulePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // What each field actually contains, fetched when a field is first chosen.
  // A text box for "Owner" means knowing a numeric user id, which nobody
  // does - the values turn a guess into a choice.
  const [values, setValues] = useState<
    Record<string, Array<{ value: string; label: string; count: number | null }>>
  >({});

  const [saved, setSaved] = useState<Segment[]>([]);
  const [saveName, setSaveName] = useState("");
  const [savedId, setSavedId] = useState<number | null>(null);
  const [savedQuery, setSavedQuery] = useState("");

  const [consentSource, setConsentSource] = useState("");
  const [consenting, setConsenting] = useState(false);
  const [consentResult, setConsentResult] = useState<{
    granted: number;
    already: number;
    refused_opted_out: number;
  } | null>(null);

  // Putting an audience into a campaign. The endpoint existed from the start
  // and only the blast builder ever called it, so the one thing a segment is
  // most obviously for could not be done from the screen that builds it.
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [enrolInto, setEnrolInto] = useState<number | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrolResult, setEnrolResult] = useState<EnrolReply | null>(null);

  const loadSaved = () =>
    api.segments().then(setSaved).catch(() => setSaved([]));

  useEffect(() => {
    api.fields().then(setFields).catch((e) => setError(e.message));
    api
      .campaigns()
      .then((cs) => setCampaigns(cs.filter((c) => c.status === "active")))
      .catch(() => setCampaigns([]));
    loadSaved();
  }, []);

  const enrol = () => {
    if (!enrolInto) return;
    const target = campaigns.find((c) => c.id === enrolInto);
    const people = preview?.reachable ?? 0;
    const steps = target?.steps?.length ?? 0;

    // One click here starts a sequence for thousands of people, and there is
    // no undo that unsends a message. So it says the two numbers that decide
    // whether this is a good idea - how many people, and how many messages
    // each - rather than asking "are you sure".
    const ok = window.confirm(
      `Start ${people.toLocaleString()} people on ${target?.name}.\n\n` +
        (steps
          ? `That is ${steps} messages each, up to ${(people * steps).toLocaleString()} in total.\n\n`
          : "") +
        "Messages already sent cannot be recalled. Continue?",
    );
    if (!ok) return;

    setEnrolling(true);
    setEnrolResult(null);
    api
      .sameAudience(rule)
      .then((check) => {
        if (check.same && !window.confirm(`${check.message}\n\nStart it anyway?`)) {
          throw new Error("Not started - that audience is already in a campaign.");
        }
        return api.enrollAudience(enrolInto, rule, undefined, undefined, undefined, check.same);
      })
      // `enrolling`, which the API has never returned, so this always read
      // undefined and the pill said "0 enrolled" however many went in.
      .then((r) => setEnrolResult(r))
      .catch((e) => setError(e.message))
      .finally(() => setEnrolling(false));
  };

  const open = (s: Segment) => {
    const asRows = fromRule(s.definition);
    if (asRows.length) setRows(asRows);
    if (s.definition?.op === "or") setJoin("or");
    else if (s.definition?.op === "and") setJoin("and");
    setSavedId(s.id);
    setSaveName(s.name);
    // On a narrow screen this list sits under the builder, so the rule it
    // just loaded is out of sight and the click looks like it did nothing.
    if (window.matchMedia("(max-width: 1100px)").matches) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const saveAudience = () => {
    const key = saveName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    api
      .createSegment({ key, name: saveName.trim(), definition: rule })
      .then((s) => {
        setSavedId(s.id);
        return loadSaved();
      })
      .catch((e) => setError(e.message));
  };

  const byKey = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.key, f])),
    [fields],
  );
  const rule = useMemo(() => toRule(rows, join), [rows, join]);

  // The saved list only grows - assistants save audiences too - so past a
  // handful it needs finding by name rather than scrolling for.
  const shownSaved = useMemo(() => {
    const q = savedQuery.trim().toLowerCase();
    if (!q) return saved;
    return saved.filter((s) =>
      `${s.name} ${s.description ?? ""}`.toLowerCase().includes(q),
    );
  }, [saved, savedQuery]);

  // Debounced, because someone adjusting a value types several characters and
  // each one would otherwise be a database count.
  useEffect(() => {
    if (!fields.length) return;
    const t = setTimeout(() => {
      setBusy(true);
      api
        .previewAudience(rule)
        .then((p) => {
          setPreview(p);
          setError(null);
        })
        .catch((e) => setError(e.message))
        .finally(() => setBusy(false));
    }, 400);
    return () => clearTimeout(t);
  }, [rule, fields.length]);

  // The single biggest reason an audience is unreachable, and the only one
  // the user can actually do something about from here.
  const noConsent = preview?.excluded?.no_consent ?? 0;

  const grantConsent = () => {
    setConsenting(true);
    setConsentResult(null);
    api
      .grantConsent(rule, consentSource.trim())
      .then((r) => {
        setConsentResult(r);
        setConsentSource("");
        return api.previewAudience(rule).then(setPreview);
      })
      .catch((e) => setError(e.message))
      .finally(() => setConsenting(false));
  };

  const update = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  // Which fields are still fetching their values, and which failed. Without
  // these a row could not tell "there is nothing to choose from" apart from
  // "the list has not arrived yet" - so for the first moment every tag row
  // showed a bare text box, and a failed fetch was cached as an empty list and
  // never asked for again until the page was reloaded.
  const [loadingVals, setLoadingVals] = useState<Record<string, boolean>>({});
  const [failedVals, setFailedVals] = useState<Record<string, boolean>>({});

  // one fetch per field, kept for as long as the page is open
  const loadValues = (key: string) => {
    if (!key || key in values || loadingVals[key]) return;
    setLoadingVals((l) => ({ ...l, [key]: true }));
    setFailedVals((f) => ({ ...f, [key]: false }));
    api
      .fieldValues(key)
      .then((rows) => setValues((v) => ({ ...v, [key]: rows })))
      .catch(() => {
        // Not cached, so the next change to this row asks again.
        setFailedVals((f) => ({ ...f, [key]: true }));
      })
      .finally(() => setLoadingVals((l) => ({ ...l, [key]: false })));
  };

  useEffect(() => {
    rows.forEach((r) => loadValues(r.field));
  }, [rows.map((r) => r.field).join(","), fields.length]);

  return (
    <div className="sidebar">
      <div className="col">
        <Card
          title="Build an audience"
          actions={
            <div className="row" style={{ gap: 8 }}>
              {rows.length > 1 && (
                <select
                  className="f"
                  style={{ fontSize: 11.5 }}
                  value={join}
                  onChange={(e) => setJoin(e.target.value as "and" | "or")}
                >
                  <option value="and">match ALL conditions</option>
                  <option value="or">match ANY condition</option>
                </select>
              )}
              <button
                className="btn s"
                onClick={() =>
                  setRows((rs) => [...rs, { field: "contact.responsiveness", op: "eq", value: "never_responded" }])
                }
              >
                + Add condition
              </button>
            </div>
          }
          footer={
            <span className="dim">
              Opted-out and blocked numbers are always excluded, whether or not you add a
              condition for them.
            </span>
          }
        >
          <ErrorBanner error={error} />
          <div style={{ marginTop: error ? 12 : 0 }}>
            {rows.map((row, i) => {
              const field = byKey[row.field];
              const ops = opsFor(field);
              const opDef = ops.find((o) => o.op === row.op) ?? ops[0];
              return (
                <div className="crow" key={i}>
                  <span className="j">{i === 0 ? "WHERE" : join.toUpperCase()}</span>

                  <select
                    className="f"
                    value={row.field}
                    onChange={(e) => {
                      const nf = byKey[e.target.value];
                      const first = opsFor(nf)[0];
                      update(i, { field: e.target.value, op: first.op, value: "" });
                    }}
                  >
                    {/* Three groups, each alphabetical. Forty-four entries in
                        one flat list meant reading all of them to find one,
                        and a custom field from the CRM looked identical to a
                        built-in. */}
                    {[
                      { label: "Tags", of: (f: FieldOption) => f.key === "tag" },
                      {
                        label: "Contact fields",
                        of: (f: FieldOption) =>
                          f.key !== "tag" && !f.key.startsWith("attr."),
                      },
                      {
                        label: "From your CRM",
                        of: (f: FieldOption) => f.key.startsWith("attr."),
                      },
                    ].map((group) => {
                      const inGroup = fields.filter(group.of);
                      if (!inGroup.length) return null;
                      return (
                        <optgroup key={group.label} label={group.label}>
                          {inGroup.map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                              {f.populated === 0 ? " (no data yet)" : ""}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>

                  <select className="f" value={row.op} onChange={(e) => update(i, { op: e.target.value })}>
                    {ops.map((o) => (
                      <option key={o.op} value={o.op}>
                        {o.label}
                      </option>
                    ))}
                  </select>

                  {opDef.value === "one" &&
                    (() => {
                      const known = values[row.field] ?? [];
                      const listId = `vals-${i}`;
                      if (field?.options?.length) {
                        return (
                          <select
                            className="f"
                            value={row.value ?? ""}
                            onChange={(e) => update(i, { value: e.target.value })}
                          >
                            <option value="">choose…</option>
                            {field.options.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        );
                      }
                      if (
                        (field?.kind === "date" || field?.kind === "datetime") &&
                        (row.op === "within" || row.op === "not_within")
                      ) {
                        // A duration, so offer durations. The box used to
                        // suggest this field's real stored dates, and every
                        // one of them was refused as "not a duration".
                        return (
                          <select
                            className="f"
                            value={row.value ?? ""}
                            onChange={(e) => update(i, { value: e.target.value })}
                          >
                            <option value="">how long…</option>
                            {/* a saved audience may use a duration that is not
                                a preset - keep it rather than blank the box */}
                            {row.value && !DURATION_PRESETS.includes(row.value) && (
                              <option value={row.value}>{row.value}</option>
                            )}
                            {DURATION_PRESETS.map((d) => (
                              <option key={d} value={d}>
                                {d
                                  .replace(/^1d$/, "1 day")
                                  .replace(/^(\d+)d$/, "$1 days")
                                  .replace(/^(\d+)m$/, "$1 months")
                                  .replace(/^1y$/, "1 year")}
                              </option>
                            ))}
                          </select>
                        );
                      }
                      // A field whose label is not its value is a reference -
                      // owner, closer, campaign. It has to be a real select:
                      // a datalist puts the OPTION'S VALUE in the box, so
                      // picking "Amar Zaidi" typed the number 12, and typing
                      // the name matched nobody.
                      if (isReference(known)) {
                        return (
                          <select
                            className="f"
                            style={{ minWidth: 170 }}
                            value={row.value ?? ""}
                            onChange={(e) => update(i, { value: e.target.value })}
                          >
                            <option value="">choose…</option>
                            {[...known]
                              // most-used first, the same order the multi-pick
                              // list uses, so the two agree
                              .sort((a, b) => (b.count ?? -1) - (a.count ?? -1))
                              .map((v) => (
                                <option key={v.value} value={v.value}>
                                  {v.count == null
                                    ? v.label
                                    : `${v.label} — ${v.count.toLocaleString()}`}
                                </option>
                              ))}
                          </select>
                        );
                      }
                      if (loadingVals[row.field] && known.length === 0) {
                        return (
                          <input className="f" style={{ width: 190 }} disabled placeholder="loading the list…" />
                        );
                      }
                      return (
                        <>
                          {/* a datalist rather than a select: the real values
                              are offered, and anything not yet in the data can
                              still be typed */}
                          <input
                            className="f"
                            list={known.length ? listId : undefined}
                            style={{ width: known.length ? 190 : 110 }}
                            value={row.value ?? ""}
                            placeholder={
                              field?.kind === "date" || field?.kind === "datetime"
                                ? "30d"
                                : known.length
                                  ? `${known.length} to choose from`
                                  : "value"
                            }
                            onChange={(e) => update(i, { value: e.target.value })}
                          />
                          {known.length > 0 && (
                            <datalist id={listId}>
                              {known.map((v) => (
                                <option key={v.value} value={v.value}>
                                  {v.count === null
                                    ? v.label
                                    : `${v.label} — ${v.count.toLocaleString()}`}
                                </option>
                              ))}
                            </datalist>
                          )}
                        </>
                      );
                    })()}

                  {opDef.value === "many" && isReference(values[row.field] ?? []) && (
                    <MultiPicker
                      options={values[row.field] ?? []}
                      value={row.value ?? ""}
                      onChange={(next) => update(i, { value: next })}
                    />
                  )}

                  {opDef.value === "many" && !isReference(values[row.field] ?? []) && (
                    (values[row.field] ?? []).length > 0 ? (
                      <MultiPicker
                        options={values[row.field] ?? []}
                        value={row.value ?? ""}
                        onChange={(next) => update(i, { value: next })}
                      />
                    ) : loadingVals[row.field] ? (
                      // Still arriving. A text box here would invite typing
                      // into something about to be replaced by a list.
                      <input className="f" style={{ width: 230 }} disabled placeholder="loading the list…" />
                    ) : (
                      // Nothing known to offer, or the list could not be
                      // fetched - a free-text list is all there is, so say
                      // which, and what shape it wants.
                      <>
                        <input
                          className="f"
                          style={{ width: 230 }}
                          value={row.value ?? ""}
                          placeholder="value, another value"
                          onChange={(e) => update(i, { value: e.target.value })}
                        />
                        <span className="dim" style={{ fontSize: 11 }}>
                          {failedVals[row.field] ? (
                            <>
                              couldn't load the list —{" "}
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  loadValues(row.field);
                                }}
                              >
                                try again
                              </a>
                            </>
                          ) : (
                            "comma separated"
                          )}
                        </span>
                      </>
                    )
                  )}

                  {opDef.value === "range" && (
                    <>
                      <input className="f" style={{ width: 70 }} value={row.min ?? ""} placeholder="min"
                             onChange={(e) => update(i, { min: e.target.value })} />
                      <span className="dim">and</span>
                      <input className="f" style={{ width: 70 }} value={row.max ?? ""} placeholder="max"
                             onChange={(e) => update(i, { max: e.target.value })} />
                    </>
                  )}

                  <button className="x" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="Ten of them, to check the rule is right"
              footer={<span className="dim">A count on its own hides a mistake. These are real people matching right now.</span>}>
          {preview?.sample?.length ? (
            <div className="tw">
              <table>
                <thead>
                  <tr><th>Name</th><th>Phone</th><th>State</th><th>Last flew</th><th>Last heard</th></tr>
                </thead>
                <tbody>
                  {preview.sample.map((s, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{String(s.name ?? "")}</td>
                      <td className="mono dim">{String(s.phone ?? "")}</td>
                      <td>{String(s.state ?? "—")}</td>
                      <td className="mono">{s.last_flight_at ? String(s.last_flight_at) : "—"}</td>
                      <td className={s.last_reply_at ? "" : "dim"}>
                        {s.last_reply_at ? fmt.when(String(s.last_reply_at)) : "never"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty">Nobody matches these conditions yet.</div>
          )}
        </Card>
      </div>

      <div className="col">
        <Card title="Audience">
          <div className="col" style={{ gap: 14 }}>
            <div>
              <span className="lab">Match the rule</span>
              <div className="num" style={{ fontSize: 31, fontWeight: 650, letterSpacing: "-0.03em" }}>
                {busy ? "…" : fmt.n(preview?.matched)}
              </div>
            </div>
            <div>
              <span className="lab">Can be messaged</span>
              <div className="num" style={{ fontSize: 26, fontWeight: 650, color: "var(--ok)" }}>
                {busy ? "…" : fmt.n(preview?.reachable)}
              </div>
            </div>

            {preview && preview.excluded_total > 0 && (
              <div>
                <span className="lab">Held back · {fmt.n(preview.excluded_total)}</span>
                <div style={{ marginTop: 6 }}>
                  {Object.entries(preview.excluded).map(([k, v]) => (
                    <div className="brk" key={k}>
                      <span className="dim">{k.replace(/_/g, " ")}</span>
                      <span>{fmt.n(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {noConsent > 0 && (
              <div className="banner warn stack">
                <b>{fmt.n(noConsent)} have no consent on file.</b> Imported contacts
                start that way — an import is not allowed to opt people in. If you
                already hold their consent, say where it came from.
                {/* stacked, not side by side: this card lives in a 320px rail
                    and a text field next to a button does not fit in it */}
                <div className="col" style={{ marginTop: 10, gap: 8 }}>
                  <input
                    className="f"
                    style={{ width: "100%" }}
                    placeholder="Where did the consent come from?"
                    value={consentSource}
                    onChange={(e) => setConsentSource(e.target.value)}
                  />
                  <button
                    className="btn p"
                    style={{ justifyContent: "center", width: "100%" }}
                    disabled={consentSource.trim().length < 3 || consenting}
                    onClick={grantConsent}
                  >
                    {consenting ? "…" : `Record consent for ${fmt.n(noConsent)}`}
                  </button>
                </div>
                <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                  e.g. “opt-in carried over from GHL”. Anyone who opted out is skipped,
                  and the change is audited against your name.
                </div>
              </div>
            )}

            {consentResult && (
              <div className="banner ok">
                {fmt.n(consentResult.granted)} now reachable
                {consentResult.already > 0 &&
                  ` · ${fmt.n(consentResult.already)} already had consent`}
                {consentResult.refused_opted_out > 0 &&
                  ` · ${fmt.n(consentResult.refused_opted_out)} skipped, they opted out`}
              </div>
            )}

            <a
              className="btn p"
              href={`/blasts/new?rule=${encodeURIComponent(JSON.stringify(rule))}`}
              style={{ justifyContent: "center" }}
            >
              Send to these people
            </a>

            <div className="col" style={{ gap: 6 }}>
              <span className="lab">Start them on a campaign</span>
              <select
                className="f"
                value={enrolInto ?? ""}
                onChange={(e) => {
                  setEnrolInto(e.target.value ? Number(e.target.value) : null);
                  setEnrolResult(null);
                }}
              >
                <option value="">choose a campaign…</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                className="btn"
                disabled={!enrolInto || enrolling || !preview?.reachable}
                onClick={enrol}
              >
                {enrolling
                  ? "Enrolling…"
                  : `Enrol ${fmt.n(preview?.reachable ?? 0)} people`}
              </button>
              {enrolResult !== null && (
                <>
                  {/* The endpoint used to answer with the number MATCHED and
                      call it "enrolled", so this said "3,738 enrolled" when
                      two thirds of them were refused for already being in a
                      campaign that cannot run alongside this one. It now
                      returns all three numbers, so the screen can stop
                      hedging and say what actually happened. */}
                  {enrolResult.status !== "done" ? (
                    <Pill tone="ac">
                      {fmt.n(enrolResult.matched)} being enrolled in the background - progress on Campaigns
                    </Pill>
                  ) : (
                  <Pill tone="ok">{fmt.n(enrolResult.enrolled)} enrolled</Pill>
                  )}
                  {enrolResult.skipped > 0 && (
                    <Pill tone="nu">{fmt.n(enrolResult.skipped)} skipped</Pill>
                  )}
                  <span className="dim" style={{ fontSize: 11 }}>
                    {enrolResult.skipped > 0
                      ? `${fmt.n(enrolResult.matched)} matched. The skipped ones are already in a campaign that cannot run alongside this one.`
                      : "Everyone who matched started the campaign."}
                  </span>
                </>
              )}
              <span className="dim" style={{ fontSize: 11 }}>
                A one-time enrolment of whoever matches right now — the campaign does
                not keep pulling people in afterwards. Anyone already in a campaign
                that cannot run alongside this one is skipped.
              </span>
            </div>

            <div className="col" style={{ gap: 6 }}>
              <span className="lab">Save this audience</span>
              <input
                className="f"
                placeholder="e.g. Repeat flyers, no reply 14d"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
              />
              <button
                className="btn"
                disabled={saveName.trim().length < 3}
                onClick={saveAudience}
              >
                {savedId ? "Save as a new one" : "Save"}
              </button>
              <span className="dim" style={{ fontSize: 11 }}>
                Saves the rule, not the list of people — so it picks up anyone new who
                matches, every time you use it.
              </span>
            </div>
          </div>
        </Card>

        <Card title={`Saved audiences · ${saved.length}`}>
          {saved.length === 0 ? (
            <div className="dim" style={{ fontSize: 12 }}>
              None yet. Save one above and it becomes reusable — for a blast, or to
              watch how that group behaves over time.
            </div>
          ) : (
            <div className="col" style={{ gap: 8 }}>
              {saved.length > 5 && (
                <input
                  className="f"
                  style={{ width: "100%" }}
                  placeholder={`Find one of ${saved.length}…`}
                  value={savedQuery}
                  onChange={(e) => setSavedQuery(e.target.value)}
                />
              )}
              <div className="saved">
                {shownSaved.map((s) => (
                  <div className={`saved-item${s.id === savedId ? " on" : ""}`} key={s.id}>
                    <button
                      className="saved-name"
                      title="Open this rule in the builder"
                      onClick={() => open(s)}
                    >
                      {s.name}
                    </button>
                    <div className="saved-meta">
                      {/* Counted once, when it was saved - nothing re-counts a
                          saved audience - so it says so rather than passing
                          for today's number. Opening it shows today's. */}
                      {s.last_reachable != null && (
                        <span title="Opening it shows how many match today">
                          {fmt.n(s.last_reachable)} reachable
                          {s.last_evaluated_at &&
                            ` on ${new Date(s.last_evaluated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
                        </span>
                      )}
                      <span className="sp" />
                      <a
                        className="btn s"
                        href={`/blasts/new?segment=${s.id}&rule=${encodeURIComponent(
                          JSON.stringify(s.definition),
                        )}`}
                      >
                        Blast
                      </a>
                      {/* A plain link, so the browser streams it straight to
                          disk rather than the page holding 38,000 rows in
                          memory waiting to hand them over. */}
                      <a
                        className="btn s"
                        title="Download these contacts as a CSV"
                        href={apiUrl(`/api/v1/segments/${s.id}/export`)}
                      >
                        CSV
                      </a>
                    </div>
                  </div>
                ))}
                {shownSaved.length === 0 && (
                  <div className="dim" style={{ fontSize: 12, padding: "8px 0" }}>
                    No saved audience matches “{savedQuery.trim()}”.
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>

        <Card title="The rule, as stored">
          <pre className="mono" style={{ fontSize: 11, margin: 0, whiteSpace: "pre-wrap", color: "var(--ink2)" }}>
            {JSON.stringify(rule, null, 2)}
          </pre>
          <div style={{ marginTop: 10 }}>
            <Pill tone="ac">Re-checked at send time</Pill>
          </div>
        </Card>
      </div>
    </div>
  );
}
